import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/editorial";
import { api } from "@/lib/api";
import type { OnboardingPayload, OnboardingResponse } from "@/types/profile";
import { HOUSEHOLD_PROFILE_KEY } from "@/features/household/useHouseholdProfile";
import { selectHouseholdPayload, useOnboarding } from "./store";
import { ONBOARDING_STEPS, type OnboardingStepId } from "./constants";
import {
  StepAllergies,
  StepAppetite,
  StepBatchSchedule,
  StepDiet,
  StepEquipment,
  StepExpertise,
  StepFoodExclusions,
  StepMeatDays,
  StepReview,
  StepSize,
} from "./steps";

export function OnboardingWizard() {
  const navigate = useNavigate();
  const step = useOnboarding((s) => s.step);
  const goNext = useOnboarding((s) => s.goNext);
  const goBack = useOnboarding((s) => s.goBack);
  // Avoid infinite loop from getSnapshot returning new reference
  // const profile = useOnboarding(selectProfile);

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const submit = useMutation({
    mutationFn: async () => {
      // 1. POST the household profile
      const onboardingRes = await api<OnboardingResponse>(
        "/api/v1/onboarding",
        {
          method: "POST",
          body: {
            household: selectHouseholdPayload(useOnboarding.getState()),
          } satisfies OnboardingPayload,
        },
      );

      // 2. Persist food exclusions. Only possible for an authenticated user
      //    (persisted=true); anonymous users keep them in the local store for
      //    the post-login sync. The bulk endpoint silently skips invalid
      //    items, so compare its returned list with what we sent and report
      //    whatever is missing instead of swallowing the failure (audit M13).
      const exclusions = useOnboarding.getState().foodExclusions;
      const rejectedExclusions = onboardingRes.persisted
        ? await saveExclusions(exclusions)
        : [];

      return { onboardingRes, rejectedExclusions };
    },
    onSuccess: ({ rejectedExclusions }) => {
      void queryClient.invalidateQueries({ queryKey: HOUSEHOLD_PROFILE_KEY });
      if (rejectedExclusions.length > 0) {
        toast.warning(t("onboarding.exclusions_not_saved"), {
          description: t("onboarding.exclusions_not_saved_detail", {
            items: rejectedExclusions.join(", "),
          }),
          duration: 10000,
        });
      }
      navigate("/menu?generate=true");
    },
  });

  const isFirst = step === ONBOARDING_STEPS[0];
  const isReview = step === "review";
  const stepIndex = ONBOARDING_STEPS.indexOf(step);
  const total = ONBOARDING_STEPS.length;

  // Resume toast: when the wizard mounts with a partial session already in
  // progress (past step 1), politely remind the user. Fire once per mount.
  const notified = useRef(false);
  useEffect(() => {
    if (notified.current) return;
    if (step !== "size") {
      notified.current = true;
      toast("Vous avez un questionnaire en cours.", {
        description:
          "Reprenez là où vous vous êtes arrêté·e, ou cliquez « Tout recommencer » ci-dessous.",
        action: {
          label: "Tout recommencer",
          onClick: () => {
            notified.current = false;
            useOnboarding.getState().reset();
          },
        },
        duration: 8000,
      });
    }
  }, [step]);

  return (
    <div className="flex min-h-full flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between px-5 pt-6 lg:px-10">
        <button
          onClick={goBack}
          disabled={isFirst}
          aria-label="Étape précédente"
          className="flex h-9 w-9 items-center justify-center text-foreground disabled:opacity-25"
        >
          <ArrowLeft className="size-5" />
        </button>
        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-ink-50">
          Étape {stepIndex + 1} / {total}
        </div>
        <Wordmark size="sm" />
      </header>

      {/* Progress bar */}
      <div className="px-5 pt-3 lg:px-10">
        <div className="h-[2px] overflow-hidden rounded-full bg-bone">
          <motion.div
            className="h-full bg-accent"
            initial={false}
            animate={{ width: `${((stepIndex + 1) / total) * 100}%` }}
            transition={{ type: "spring", stiffness: 150, damping: 25 }}
          />
        </div>
      </div>

      {/* Step body */}
      <div className="flex flex-1 items-center justify-center px-5 py-10 lg:px-10 lg:py-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            <StepView
              step={step}
              onSubmit={() => submit.mutate()}
              isSubmitting={submit.isPending}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {submit.isError && (
        <p className="mx-auto max-w-md px-5 pb-2 text-center text-sm text-destructive">
          Erreur : {(submit.error as Error).message}
        </p>
      )}

      {/* Footer nav */}
      {!isReview && (
        <nav className="border-t border-hair bg-background px-5 py-5 lg:px-10">
          <div className="mx-auto flex w-full max-w-md justify-end">
            <Button size="lg" onClick={goNext} className="w-full sm:w-auto">
              Continuer <ArrowRight />
            </Button>
          </div>
        </nav>
      )}
    </div>
  );
}

/**
 * Save the onboarding exclusions through the bulk endpoint and return the
 * items that did NOT make it (invalid characters, cap reached, network
 * error). Items already excluded count as saved: the backend returns the
 * full list, so a case-insensitive membership check covers them.
 */
async function saveExclusions(items: string[]): Promise<string[]> {
  if (items.length === 0) return [];
  try {
    const saved = await api<string[]>("/api/v1/food-exclusions/bulk", {
      method: "POST",
      body: { items },
    });
    const savedKeys = new Set(saved.map(exclusionKey));
    return items.filter((i) => !savedKeys.has(exclusionKey(i)));
  } catch {
    return items;
  }
}

/** Same canonical form as the backend: NFC, trimmed, case-insensitive. */
function exclusionKey(item: string): string {
  return item.normalize("NFC").trim().toLowerCase();
}

function StepView({
  step,
  onSubmit,
  isSubmitting,
}: {
  step: OnboardingStepId;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  switch (step) {
    case "size":
      return <StepSize />;
    case "diet":
      return <StepDiet />;
    case "meat-days":
      return <StepMeatDays />;
    case "allergies":
      return <StepAllergies />;
    case "equipment":
      return <StepEquipment />;
    case "expertise":
      return <StepExpertise />;
    case "appetite":
      return <StepAppetite />;
    case "batch-schedule":
      return <StepBatchSchedule />;
    case "food-exclusions":
      return <StepFoodExclusions />;
    case "review":
      return <StepReview onSubmit={onSubmit} isSubmitting={isSubmitting} />;
  }
}
